/**
 * Авторские стикеры ANZONIX.
 *
 * Не кружки-значки, а настоящий стикерпак: каждый — персонаж или
 * предмет из жизни трейдера, вырезанный по контуру, с белой кромкой,
 * мягкой тенью и подписью на ярлыке. С юмором, который поймёт любой,
 * кто хоть раз ловил нож или «переводил сделку в долгосрок».
 *
 * Все в одном стиле: плотная тёмная обводка, фирменные цвета, ярлык
 * наискосок. Поэтому стикер на скриншоте узнаётся как наш.
 *
 * Рисуются картинкой из SVG: картинки собираются один раз при загрузке,
 * а холст потом рисует их без задержки. Подписи — системным жирным
 * шрифтом: внешние шрифты в картинку-SVG не попадают.
 */

export interface Sticker {
  readonly id: string;
  /** Имя в палитре — коротко, что это. */
  readonly name: string;
  /** Фирменный цвет стикера: им же окрашен ярлык. */
  readonly color: string;
  /** Рисунок, координаты 0…120; ярлык — отдельно. */
  readonly art: string;
  /** Подпись на ярлыке: одна или две строки. */
  readonly caption: readonly string[];
  /** Наклон, градусы: стикеры налеплены не по линейке. */
  readonly tilt: number;
}

/** Тёмная обводка — как у нарисованного от руки стикера. */
const INK = '#0B1122';
/** Заливка с обводкой. */
const o = (fill: string, width = 2.6): string =>
  `fill="${fill}" stroke="${INK}" stroke-width="${width}" stroke-linejoin="round" stroke-linecap="round"`;
/** Только линия. */
const l = (color = INK, width = 2.6): string =>
  `fill="none" stroke="${color}" stroke-width="${width}" stroke-linejoin="round" stroke-linecap="round"`;
/** Блик. */
const shine = (d: string): string => `<path d="${d}" fill="none" stroke="#FFFFFF" stroke-opacity="0.55" stroke-width="2.4" stroke-linecap="round"/>`;

/** Звезда-взрыв: n лучей между радиусами outer и inner. */
function burst(cx: number, cy: number, outer: number, inner: number, n: number, turn = 0): string {
  const points: string[] = [];
  for (let i = 0; i < n * 2; i += 1) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (Math.PI * i) / n + turn;
    points.push(`${(cx + r * Math.sin(a)).toFixed(1)},${(cy - r * Math.cos(a)).toFixed(1)}`);
  }
  return points.join(' ');
}

/** Искорка-звёздочка на четыре луча. */
const spark = (x: number, y: number, r: number, color = '#FFFFFF'): string =>
  `<path d="M${x} ${y - r}Q${x} ${y} ${x + r} ${y}Q${x} ${y} ${x} ${y + r}Q${x} ${y} ${x - r} ${y}Q${x} ${y} ${x} ${y - r}Z" fill="${color}"/>`;

export const STICKERS: readonly Sticker[] = [
  {
    id: 'bull',
    name: 'Бык',
    color: '#2EE6B0',
    caption: ['БЫЧИМ!'],
    tilt: -5,
    art: `
      <path d="M40 30C30 28 21 21 19 9c8 6 17 9 26 12z" ${o('#F4E7C8')}/>
      <path d="M80 30c10-2 19-9 21-21-8 6-17 9-26 12z" ${o('#F4E7C8')}/>
      <ellipse cx="29" cy="37" rx="10" ry="5.5" transform="rotate(-22 29 37)" ${o('#2EE6B0')}/>
      <ellipse cx="91" cy="37" rx="10" ry="5.5" transform="rotate(22 91 37)" ${o('#2EE6B0')}/>
      <path d="M38 26c8-6 36-6 44 0 6 8 4 24-2 34-5 8-12 12-20 12s-15-4-20-12c-6-10-8-26-2-34z" ${o('#2EE6B0')}/>
      <ellipse cx="60" cy="61" rx="17" ry="11" ${o('#B6F7E4')}/>
      <ellipse cx="54" cy="61" rx="2.4" ry="3" fill="${INK}"/><ellipse cx="66" cy="61" rx="2.4" ry="3" fill="${INK}"/>
      <rect x="37" y="36" width="21" height="13" rx="5" fill="${INK}"/><rect x="62" y="36" width="21" height="13" rx="5" fill="${INK}"/>
      <path d="M58 40h4" ${l(INK, 3)}/>
      <path d="M41 40l6 0M66 40l6 0" ${l('#67E8F9', 2)}/>
      <circle cx="60" cy="72.5" r="4.2" ${l('#F5C04E', 2.6)}/>
      ${shine('M44 28c4-2 9-3 14-3')}`,
  },
  {
    id: 'bear',
    name: 'Медведь',
    color: '#FF5470',
    caption: ['ШОРТИМ'],
    tilt: 4,
    art: `
      <circle cx="33" cy="24" r="11" ${o('#FF6B81')}/><circle cx="87" cy="24" r="11" ${o('#FF6B81')}/>
      <circle cx="33" cy="24" r="5" fill="#FFD1D9"/><circle cx="87" cy="24" r="5" fill="#FFD1D9"/>
      <ellipse cx="60" cy="47" rx="31" ry="28" ${o('#FF6B81')}/>
      <ellipse cx="60" cy="58" rx="14" ry="11" ${o('#FFD1D9')}/>
      <ellipse cx="60" cy="52.5" rx="5.5" ry="3.8" fill="${INK}"/>
      <path d="M42 34l11 3M78 33l-11 4" ${l(INK, 3)}/>
      <path d="M43 44q5-4 11 0M66 44q5-4 11 0" ${l(INK, 3)}/>
      <path d="M53 62q7 5 14-2" ${l(INK, 2.6)}/>
      <path d="M98 50v22M89 63l9 10 9-10" ${l('#FF5470', 4)}/>
      ${shine('M40 30c4-4 9-6 15-7')}`,
  },
  {
    id: 'whale',
    name: 'Кит',
    color: '#3BA7FF',
    caption: ['КИТ ЗАШЁЛ'],
    tilt: -3,
    art: `
      <path d="M52 22c0-7 4-12 9-15M52 22c-3-6-9-9-14-8" ${l('#67E8F9', 3.2)}/>
      <circle cx="64" cy="6" r="2.6" fill="#67E8F9"/><circle cx="34" cy="13" r="2.4" fill="#67E8F9"/>
      <path d="M14 52c0-16 16-26 38-26 22 0 36 12 38 24l10-8c4-2 7 1 5 5l-5 9 6 8c2 4-2 7-6 4l-10-6c-4 10-18 16-36 16-24 0-40-11-40-26z" ${o('#3BA7FF')}/>
      <path d="M22 62c10 11 34 13 54 5-12 6-38 6-54-5z" ${o('#BFE3FF', 2.2)}/>
      <circle cx="35" cy="46" r="4" fill="${INK}"/><circle cx="36.3" cy="44.6" r="1.3" fill="#FFFFFF"/>
      <path d="M24 55q8 6 16 1" ${l(INK, 2.6)}/>
      ${shine('M28 34c6-4 14-6 22-6')}`,
  },
  {
    id: 'rocket',
    name: 'Ракета',
    color: '#A78BFA',
    caption: ['ПОЕХАЛИ!'],
    tilt: 6,
    art: `
      <path d="M52 64c0 8 4 12 8 17 4-5 8-9 8-17z" ${o('#FF8A3D')}/>
      <path d="M56 64c0 5 2 8 4 11 2-3 4-6 4-11z" fill="#F5C04E"/>
      <path d="M46 46L31 61l15 2zM74 46l15 15-15 2z" ${o('#A78BFA')}/>
      <path d="M60 5c14 10 20 27 16 49l-5 10H49l-5-10C40 32 46 15 60 5z" ${o('#ECE8FF')}/>
      <path d="M60 5c6 4 10 9 12 15H48c2-6 6-11 12-15z" ${o('#A78BFA')}/>
      <circle cx="60" cy="35" r="8.5" ${o('#67E8F9')}/>
      <path d="M56 32a5 5 0 0 1 5-3" ${l('#FFFFFF', 2)}/>
      <path d="M52 58h16" ${l(INK, 2.2)}/>
      ${spark(24, 26, 5)}${spark(96, 20, 4)}${spark(98, 48, 3, '#F5C04E')}`,
  },
  {
    id: 'moon',
    name: 'На Луну',
    color: '#F5C04E',
    caption: ['TO THE MOON'],
    tilt: -4,
    art: `
      <circle cx="60" cy="43" r="33" ${o('#FFE08A')}/>
      <circle cx="42" cy="30" r="6" ${o('#F5C04E', 2)}/><circle cx="80" cy="58" r="5" ${o('#F5C04E', 2)}/><circle cx="76" cy="26" r="3.5" ${o('#F5C04E', 2)}/>
      <path d="M45 44q5-5 10 0M65 44q5-5 10 0" ${l(INK, 3)}/>
      <path d="M48 53q12 11 24 0z" ${o('#FF6B81', 2.4)}/>
      <ellipse cx="44" cy="52" rx="4" ry="2.4" fill="#FF9DB0" opacity="0.8"/><ellipse cx="76" cy="52" rx="4" ry="2.4" fill="#FF9DB0" opacity="0.8"/>
      <path d="M95 6l6 3-2 8-6 1-2-4z" ${o('#ECE8FF', 2)}/>
      <path d="M92 16l-6 6" ${l('#FF8A3D', 3)}/>
      ${spark(18, 18, 5)}${spark(104, 38, 4)}${spark(16, 62, 3.5, '#FFE08A')}
      ${shine('M36 24c5-6 12-9 20-9')}`,
  },
  {
    id: 'liquidation',
    name: 'Ликвидация',
    color: '#FF8A3D',
    caption: ['ЛИКВИДНУЛИ'],
    tilt: 3,
    art: `
      <polygon points="${burst(60, 44, 40, 27, 12)}" ${o('#FF8A3D')}/>
      <polygon points="${burst(60, 44, 27, 18, 12, 0.26)}" fill="#F5C04E"/>
      <path d="M46 40c0-11 6-16 14-16s14 5 14 16c0 6-3 9-6 11v6H52v-6c-3-2-6-5-6-11z" ${o('#F4F7FF')}/>
      <circle cx="54" cy="40" r="4" fill="${INK}"/><circle cx="66" cy="40" r="4" fill="${INK}"/>
      <path d="M60 45l-2 4h4z" fill="${INK}"/>
      <path d="M55 57v-4M60 57v-4M65 57v-4" ${l(INK, 2)}/>`,
  },
  {
    id: 'knife',
    name: 'Ловит ножи',
    color: '#FF5470',
    caption: ['ЛОВЛЮ НОЖ'],
    tilt: -6,
    art: `
      <rect x="50" y="4" width="12" height="20" rx="3" ${o('#6B4A3A')}/>
      <circle cx="56" cy="10" r="1.6" fill="#F5C04E"/><circle cx="56" cy="18" r="1.6" fill="#F5C04E"/>
      <rect x="46" y="22" width="20" height="5" rx="2" ${o('#9AA6BD', 2.2)}/>
      <path d="M49 27h14v22l-7 12-7-12z" ${o('#E6EDF7')}/>
      <path d="M53 30v18" ${l('#FFFFFF', 2)}/>
      <path d="M30 28l-8-4M28 40l-10 0M88 26l8-5M90 38l10-1" ${l('#FF5470', 3)}/>
      <path d="M24 70c0-7 6-11 13-10l30 4c6 1 7 7 3 9l-10 1h24c6 0 6 9 0 9H40c-9 0-16-5-16-13z" ${o('#FFD6B8')}/>
      <rect x="44" y="62" width="18" height="8" rx="3" transform="rotate(-12 53 66)" ${o('#F5C9A0', 2)}/>
      <path d="M49 64l8 2M50 67l7 1" ${l('#D99A6C', 1.4)}/>
      <path d="M92 50c0 4-3 6-5 6s-5-2-5-6c0-3 5-9 5-9s5 6 5 9z" ${o('#8FD3FF', 2)}/>`,
  },
  {
    id: 'target',
    name: 'Цель',
    color: '#2EE6B0',
    caption: ['ТЕЙК ТУТ'],
    tilt: 4,
    art: `
      <circle cx="56" cy="46" r="33" ${o('#F4F7FF')}/>
      <circle cx="56" cy="46" r="25" ${o('#2EE6B0', 2.2)}/>
      <circle cx="56" cy="46" r="16" ${o('#F4F7FF', 2.2)}/>
      <circle cx="56" cy="46" r="8" ${o('#FF5470', 2.2)}/>
      <path d="M57 45L94 12" ${l(INK, 3.4)}/>
      <path d="M88 10l12-6-4 12zM92 16l12-2-8 8z" ${o('#A78BFA', 2)}/>
      ${spark(24, 16, 5)}${spark(20, 74, 4, '#2EE6B0')}`,
  },
  {
    id: 'stop',
    name: 'Стоп',
    color: '#FF5470',
    caption: ['СТОП?', 'НЕ СЛЫШАЛ'],
    tilt: -4,
    art: `
      <polygon points="${burst(60, 42, 34, 34, 4, Math.PI / 8)}" ${o('#FF5470')}/>
      <polygon points="${burst(60, 42, 28, 28, 4, Math.PI / 8)}" fill="none" stroke="#FFFFFF" stroke-width="2.4"/>
      <ellipse cx="50" cy="38" rx="7" ry="8" ${o('#FFFFFF', 2.2)}/><ellipse cx="70" cy="38" rx="7" ry="8" ${o('#FFFFFF', 2.2)}/>
      <circle cx="54" cy="36" r="3.2" fill="${INK}"/><circle cx="74" cy="36" r="3.2" fill="${INK}"/>
      <circle cx="61" cy="55" r="4" ${o('#FFD1D9', 2.2)}/>
      <path d="M88 20c0 4-3 6-5 6s-5-2-5-6c0-3 5-9 5-9s5 6 5 9z" ${o('#8FD3FF', 2)}/>
      <path d="M96 48q4-3 8 0M98 56q4-3 8 0" ${l('#FFFFFF', 2.2)}/>`,
  },
  {
    id: 'entry',
    name: 'Вход тут',
    color: '#3BA7FF',
    caption: ['ЗАХОДИМ!'],
    tilt: 5,
    art: `
      <rect x="52" y="44" width="8" height="38" rx="2" ${o('#8A6A4A')}/>
      <path d="M18 18h66l16 15-16 15H18z" ${o('#3BA7FF')}/>
      <text x="50" y="40" text-anchor="middle" font-family="'Arial Black','Segoe UI Black',Arial,sans-serif" font-weight="900" font-size="17" fill="#FFFFFF" stroke="${INK}" stroke-width="0.8">ВХОД</text>
      <path d="M86 30l6 3-6 3" ${l('#FFFFFF', 2.6)}/>
      ${spark(14, 60, 5, '#67E8F9')}${spark(100, 62, 4)}`,
  },
  {
    id: 'retest',
    name: 'Жду ретест',
    color: '#67E8F9',
    caption: ['ЖДУ РЕТЕСТ'],
    tilt: -3,
    art: `
      <path d="M12 74c0-6 6-8 14-8h58c6 0 10-4 10-10 0-6 6-8 8-2 3 10-5 22-17 22H18c-4 0-6-1-6-2z" ${o('#9BE7F5')}/>
      <path d="M96 52l-2-16M104 52l4-15" ${l(INK, 2.4)}/>
      <circle cx="93" cy="34" r="4.5" ${o('#FFFFFF', 2)}/><circle cx="108" cy="35" r="4.5" ${o('#FFFFFF', 2)}/>
      <path d="M90 34h6M105 35h6" ${l(INK, 2.4)}/>
      <circle cx="54" cy="46" r="23" ${o('#67E8F9')}/>
      <path d="M54 46a4 4 0 1 1 4 4 9 9 0 1 1-9-9 15 15 0 1 1 15 15" ${l(INK, 2.4)}/>
      <text x="20" y="22" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="12" fill="#FFFFFF" stroke="${INK}" stroke-width="0.7">z</text>
      <text x="28" y="14" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="9" fill="#FFFFFF" stroke="${INK}" stroke-width="0.6">z</text>
      ${shine('M40 32c4-4 9-6 14-6')}`,
  },
  {
    id: 'take',
    name: 'Фиксирую',
    color: '#F5C04E',
    caption: ['ФИКСАНУЛ'],
    tilt: 4,
    art: `
      <path d="M44 30c-8 10-14 22-12 34 2 12 14 16 28 16s26-4 28-16c2-12-4-24-12-34z" ${o('#F5C04E')}/>
      <path d="M46 20l6 10h16l6-10c-8 2-20 2-28 0z" ${o('#E0A93A')}/>
      <path d="M48 30h24" ${l(INK, 3)}/>
      <text x="60" y="66" text-anchor="middle" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="26" fill="${INK}">$</text>
      <ellipse cx="22" cy="74" rx="9" ry="4" ${o('#FFE08A', 2)}/><ellipse cx="22" cy="69" rx="9" ry="4" ${o('#FFE08A', 2)}/>
      <ellipse cx="98" cy="72" rx="9" ry="4" ${o('#FFE08A', 2)}/>
      ${spark(96, 20, 5)}${spark(18, 30, 4, '#FFE08A')}
      ${shine('M42 46c1-5 3-9 6-12')}`,
  },
  {
    id: 'fire',
    name: 'Огонь',
    color: '#FF8A3D',
    caption: ['ВСЁ ПОД', 'КОНТРОЛЕМ'],
    tilt: -5,
    art: `
      <path d="M52 6c6 14 20 20 20 40 0 18-10 30-20 30S32 64 32 46c0-12 8-18 10-28 4 8 6 12 8 12 0-8 0-16 2-24z" ${o('#FF8A3D')}/>
      <path d="M52 34c4 8 12 12 12 24 0 10-6 16-12 16s-12-6-12-16c0-6 4-9 5-14 2 4 3 6 5 6 0-6 1-10 2-16z" fill="#F5C04E"/>
      <path d="M44 54q3-3 6 0M55 54q3-3 6 0" ${l(INK, 2.6)}/>
      <path d="M48 62q4 3 8 0" ${l(INK, 2.4)}/>
      <path d="M78 50h18v18c0 4-3 7-7 7h-4c-4 0-7-3-7-7z" ${o('#F4F7FF')}/>
      <path d="M96 55c6 0 6 10 0 10" ${l(INK, 2.6)}/>
      <path d="M84 44c-2-4 2-6 0-10M91 44c-2-4 2-6 0-10" ${l('#C9D3E6', 2.2)}/>`,
  },
  {
    id: 'eyes',
    name: 'Слежу',
    color: '#A78BFA',
    caption: ['Я ТОЛЬКО', 'ПОСМОТРЕТЬ'],
    tilt: 3,
    art: `
      <ellipse cx="46" cy="42" rx="13" ry="16" ${o('#FFFFFF')}/><ellipse cx="74" cy="42" rx="13" ry="16" ${o('#FFFFFF')}/>
      <circle cx="41" cy="44" r="6.5" fill="${INK}"/><circle cx="69" cy="44" r="6.5" fill="${INK}"/>
      <circle cx="43" cy="41.5" r="2" fill="#FFFFFF"/><circle cx="71" cy="41.5" r="2" fill="#FFFFFF"/>
      <path d="M12 54h96v26H12z" ${o('#A78BFA')}/>
      <path d="M12 67h96M36 54v13M68 54v13M52 67v13M86 67v13" ${l('#7C66D6', 2)}/>
      <path d="M28 54c0-5 3-7 6-7s4 3 4 7M82 54c0-5 3-7 6-7s4 3 4 7" ${o('#FFD6B8', 2.2)}/>`,
  },
  {
    id: 'hodl',
    name: 'HODL',
    color: '#67E8F9',
    caption: ['HODL'],
    tilt: -4,
    art: `
      <path d="M34 22h52l16 18-42 42-42-42z" ${o('#67E8F9')}/>
      <path d="M18 40h84M34 22l-4 18 30 42 30-42-4-18M46 22l-4 18 18 42 18-42-4-18" ${l(INK, 2)}/>
      <path d="M8 44c0-8 8-12 14-8l8 6c3 3 1 9-3 9h-8c-6 0-11-2-11-7zM112 44c0-8-8-12-14-8l-8 6c-3 3-1 9 3 9h8c6 0 11-2 11-7z" ${o('#FFD6B8')}/>
      ${spark(60, 8, 5)}${spark(98, 16, 4)}${spark(22, 18, 3.5, '#67E8F9')}
      ${shine('M40 28l8 0')}`,
  },
  {
    id: 'fomo',
    name: 'FOMO',
    color: '#F5C04E',
    caption: ['ПОЕЗД УШЁЛ'],
    tilt: 5,
    art: `
      <circle cx="30" cy="12" r="6" ${o('#E3E9F4', 2)}/><circle cx="20" cy="8" r="4" ${o('#E3E9F4', 2)}/>
      <rect x="32" y="18" width="10" height="16" rx="2" ${o('#6B7890')}/>
      <rect x="22" y="34" width="54" height="30" rx="6" ${o('#F5C04E')}/>
      <path d="M70 20h22v44H70z" ${o('#FF8A3D')}/>
      <rect x="75" y="26" width="12" height="11" rx="2" ${o('#67E8F9', 2)}/>
      <path d="M22 44h48" ${l(INK, 2.2)}/>
      <circle cx="34" cy="68" r="8" ${o('#3A4560')}/><circle cx="58" cy="68" r="8" ${o('#3A4560')}/><circle cx="82" cy="68" r="7" ${o('#3A4560')}/>
      <circle cx="34" cy="68" r="2.4" fill="#F4F7FF"/><circle cx="58" cy="68" r="2.4" fill="#F4F7FF"/><circle cx="82" cy="68" r="2" fill="#F4F7FF"/>
      <path d="M4 46h12M8 56h10M2 36h10" ${l('#F5C04E', 3)}/>`,
  },
  {
    id: 'hamster',
    name: 'Хомяк',
    color: '#F5B97A',
    caption: ['КУПИЛ', 'НА ХАЯХ'],
    tilt: -3,
    art: `
      <circle cx="38" cy="18" r="8" ${o('#F5B97A')}/><circle cx="82" cy="18" r="8" ${o('#F5B97A')}/>
      <circle cx="38" cy="18" r="3.6" fill="#FFB3C1"/><circle cx="82" cy="18" r="3.6" fill="#FFB3C1"/>
      <ellipse cx="60" cy="48" rx="34" ry="32" ${o('#F5B97A')}/>
      <ellipse cx="60" cy="58" rx="22" ry="20" fill="#FFE3C4"/>
      <ellipse cx="34" cy="50" rx="11" ry="10" ${o('#FFD6A8', 2.2)}/><ellipse cx="86" cy="50" rx="11" ry="10" ${o('#FFD6A8', 2.2)}/>
      <circle cx="48" cy="36" r="4.4" fill="${INK}"/><circle cx="72" cy="36" r="4.4" fill="${INK}"/>
      <circle cx="49.5" cy="34.5" r="1.5" fill="#FFFFFF"/><circle cx="73.5" cy="34.5" r="1.5" fill="#FFFFFF"/>
      <path d="M56 45l4 3 4-3" ${l(INK, 2.4)}/>
      <circle cx="60" cy="66" r="11" ${o('#F5C04E')}/>
      <text x="60" y="71" text-anchor="middle" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="13" fill="${INK}">₿</text>
      <path d="M100 12l0 16M94 20l6-8 6 8" ${l('#2EE6B0', 3)}/>`,
  },
  {
    id: 'dig',
    name: 'Копаем дно',
    color: '#FF5470',
    caption: ['КОПАЕМ ДНО'],
    tilt: 4,
    art: `
      <ellipse cx="54" cy="72" rx="40" ry="10" ${o('#2A1F3D')}/>
      <path d="M86 70c4-8 10-10 18-8 4 2 4 6 0 8z" ${o('#8A6A4A', 2.2)}/>
      <path d="M78 8L60 60" ${l('#8A6A4A', 5)}/>
      <path d="M72 6h14" ${l(INK, 4)}/>
      <path d="M50 54h18l-3 16c-3 5-10 5-13 0z" transform="rotate(20 59 62)" ${o('#C9D3E6')}/>
      <rect x="26" y="30" width="10" height="26" rx="2" ${o('#FF5470', 2.2)}/>
      <path d="M31 22v8M31 56v6" ${l(INK, 2.2)}/>
      <path d="M40 36l6 10M46 46l-8-1" ${l('#FF5470', 3)}/>`,
  },
  {
    id: 'tilt',
    name: 'Тильт',
    color: '#FF5470',
    caption: ['Я В ТИЛЬТЕ'],
    tilt: -6,
    art: `
      <path d="M60 10v12M60 74v8" ${l(INK, 3)}/>
      <rect x="40" y="22" width="40" height="52" rx="7" ${o('#FF5470')}/>
      <path d="M50 40a3 3 0 1 1 3 3 6 6 0 1 1-6-6M70 40a3 3 0 1 1 3 3 6 6 0 1 1-6-6" ${l(INK, 2.2)}/>
      <path d="M48 60q3-4 6 0t6 0 6 0 6 0" ${l(INK, 2.4)}/>
      <circle cx="30" cy="14" r="6" ${o('#E3E9F4', 2)}/><circle cx="90" cy="12" r="7" ${o('#E3E9F4', 2)}/><circle cx="100" cy="24" r="4" ${o('#E3E9F4', 2)}/>
      <path d="M22 44l-8 4 8 4M98 44l8 4-8 4" ${l('#F5C04E', 3)}/>`,
  },
  {
    id: 'pump',
    name: 'Памп',
    color: '#2EE6B0',
    caption: ['ПАМП!'],
    tilt: 4,
    art: `
      <path d="M60 8v14M60 72v8" ${l(INK, 3)}/>
      <path d="M44 38c-10 0-18-6-20-16-1-6 6-8 8-3 2 5 6 9 12 9M76 38c10 0 18-6 20-16 1-6-6-8-8-3-2 5-6 9-12 9" ${o('#2EE6B0')}/>
      <circle cx="26" cy="20" r="6" ${o('#2EE6B0')}/><circle cx="94" cy="20" r="6" ${o('#2EE6B0')}/>
      <rect x="42" y="22" width="36" height="50" rx="7" ${o('#2EE6B0')}/>
      <path d="M49 38l8 3M71 38l-8 3" ${l(INK, 3)}/>
      <circle cx="53" cy="45" r="3" fill="${INK}"/><circle cx="67" cy="45" r="3" fill="${INK}"/>
      <path d="M52 56q8 7 16 0z" ${o('#FFFFFF', 2.2)}/>
      ${shine('M48 30v12')}
      ${spark(106, 44, 5)}${spark(14, 44, 4)}`,
  },
  {
    id: 'parachute',
    name: 'Дамп',
    color: '#FF5470',
    caption: ['МЯГКАЯ', 'ПОСАДКА'],
    tilt: -4,
    art: `
      <path d="M20 34C20 12 100 12 100 34c-6-4-14-4-20 0-6-4-14-4-20 0-6-4-14-4-20 0-6-4-14-4-20 0z" ${o('#F4F7FF')}/>
      <path d="M40 34C40 16 60 12 60 12s20 4 20 22c-6-4-14-4-20 0-6-4-14-4-20 0z" fill="#FF5470" stroke="${INK}" stroke-width="2.2" stroke-linejoin="round"/>
      <path d="M22 35l30 23M60 34v22M98 35L68 58" ${l(INK, 1.6)}/>
      <path d="M60 50v6M60 80v4" ${l(INK, 2.4)}/>
      <rect x="50" y="56" width="20" height="24" rx="4" ${o('#FF5470')}/>
      <circle cx="56" cy="65" r="2.4" fill="${INK}"/><circle cx="64" cy="65" r="2.4" fill="${INK}"/>
      <ellipse cx="60" cy="73" rx="3" ry="2.4" fill="${INK}"/>
      <path d="M84 54c0 4-3 6-5 6s-5-2-5-6c0-3 5-9 5-9s5 6 5 9z" ${o('#8FD3FF', 2)}/>`,
  },
  {
    id: 'crab',
    name: 'Боковик',
    color: '#FF7A59',
    caption: ['КРАБИМ'],
    tilt: 3,
    art: `
      <path d="M36 8H84M36 8l6-5M36 8l6 5M84 8l-6-5M84 8l-6 5" ${l('#F5C04E', 3)}/>
      <path d="M40 60l-12 14M46 64l-8 14M80 60l12 14M74 64l8 14" ${l(INK, 3)}/>
      <path d="M36 40c-10-2-18-10-16-20 6 0 10 4 12 8 0-6 4-10 10-10-2 8-2 16-6 22zM84 40c10-2 18-10 16-20-6 0-10 4-12 8 0-6-4-10-10-10 2 8 2 16 6 22z" ${o('#FF7A59')}/>
      <ellipse cx="60" cy="50" rx="28" ry="19" ${o('#FF7A59')}/>
      <path d="M52 34l-2-10M68 34l2-10" ${l(INK, 2.4)}/>
      <circle cx="50" cy="22" r="5" ${o('#FFFFFF', 2)}/><circle cx="70" cy="22" r="5" ${o('#FFFFFF', 2)}/>
      <circle cx="48" cy="22" r="2.2" fill="${INK}"/><circle cx="68" cy="22" r="2.2" fill="${INK}"/>
      <path d="M52 54q8 5 16 0" ${l(INK, 2.4)}/>
      ${shine('M42 42c4-3 9-5 14-5')}`,
  },
  {
    id: 'lambo',
    name: 'Ламбо',
    color: '#F5C04E',
    caption: ['ГДЕ ЛАМБО?'],
    tilt: -5,
    art: `
      <path d="M8 60l10-12 28-8h26l24 10 16 6v10H8z" ${o('#F5C04E')}/>
      <path d="M48 42l4-0 18 0 16 8H42z" ${o('#1D2A44', 2.2)}/>
      <path d="M14 58h20M92 58h14" ${l(INK, 2.2)}/>
      <circle cx="30" cy="68" r="9" ${o('#3A4560')}/><circle cx="90" cy="68" r="9" ${o('#3A4560')}/>
      <circle cx="30" cy="68" r="3" fill="#C9D3E6"/><circle cx="90" cy="68" r="3" fill="#C9D3E6"/>
      <text x="96" y="30" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="22" fill="#FFFFFF" stroke="${INK}" stroke-width="1.2">?</text>
      <text x="14" y="30" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="16" fill="#FFFFFF" stroke="${INK}" stroke-width="1">?</text>
      ${shine('M50 46h14')}`,
  },
  {
    id: 'longterm',
    name: 'Долгосрок',
    color: '#2EE6B0',
    caption: ['ТЕПЕРЬ ЭТО', 'ИНВЕСТИЦИЯ'],
    tilt: 4,
    art: `
      <path d="M60 54V26" ${l('#1FA37E', 3.4)}/>
      <path d="M60 36c-4-12-18-16-26-12 4 10 16 14 26 12zM60 30c4-12 18-16 26-12-4 10-16 14-26 12z" ${o('#2EE6B0')}/>
      <path d="M60 22c-4-8 0-16 6-18 2 8-2 14-6 18z" ${o('#2EE6B0')}/>
      <path d="M36 54h48l-6 28H42z" ${o('#C97B4A')}/>
      <path d="M34 54h52v8H34z" ${o('#E0955E')}/>
      <path d="M88 26v22" ${l('#8A6A4A', 2.6)}/>
      <rect x="80" y="14" width="30" height="16" rx="3" ${o('#F4F7FF', 2.2)}/>
      <text x="95" y="26" text-anchor="middle" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="10" fill="#FF5470">−80%</text>`,
  },
  // --- чёрный юмор трейдера ------------------------------------------------
  {
    id: 'rip',
    name: 'RIP депо',
    color: '#B7C2D9',
    caption: ['RIP ДЕПО'],
    tilt: -4,
    art: `
      <path d="M12 82c10-12 86-12 96 0z" ${o('#2A1F3D')}/>
      <path d="M34 80V36c0-17 12-28 26-28s26 11 26 28v44z" ${o('#C9D3E6')}/>
      <path d="M40 36c0-12 8-20 18-21" ${l('#FFFFFF', 2.4)}/>
      <text x="60" y="42" text-anchor="middle" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="15" fill="${INK}">RIP</text>
      <text x="60" y="56" text-anchor="middle" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="8.5" fill="#3A4560">2021–2026</text>
      <text x="60" y="70" text-anchor="middle" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="10" fill="#FF5470">x100</text>
      <path d="M22 80V68M98 80V66" ${l('#2EE6B0', 2.4)}/>
      <circle cx="22" cy="65" r="4.5" ${o('#FF6B81', 2)}/><circle cx="98" cy="63" r="4.5" ${o('#F5C04E', 2)}/>
      <path d="M72 12l4-6M80 18l7-3" ${l('#C9D3E6', 2.2)}/>`,
  },
  {
    id: 'kidney',
    name: 'Почка',
    color: '#FF6B81',
    caption: ['ПОЧКА ЕСТЬ —', 'ДОЛИВАЮСЬ'],
    tilt: 5,
    art: `
      <path d="M40 16c14-10 42-4 48 16 6 22-6 44-26 46-14 1-18-10-12-18 4-5 4-11-2-15-12-8-18-21-8-29z" ${o('#FF6B81')}/>
      <path d="M48 20c8-5 20-4 28 2" ${l('#FFFFFF', 2.4)}/>
      <circle cx="62" cy="38" r="4" fill="${INK}"/><circle cx="78" cy="36" r="4" fill="${INK}"/>
      <circle cx="63.4" cy="36.6" r="1.3" fill="#FFFFFF"/><circle cx="79.4" cy="34.6" r="1.3" fill="#FFFFFF"/>
      <path d="M64 50q6 4 12-1" ${l(INK, 2.4)}/>
      <path d="M86 20c0 4-3 6-5 6s-5-2-5-6c0-3 5-9 5-9s5 6 5 9z" ${o('#8FD3FF', 2)}/>
      <path d="M40 50l-14 12" ${l(INK, 1.6)}/>
      <path d="M10 60l16-4 6 14-16 4z" ${o('#F5C04E', 2.2)}/>
      <text x="21" y="70" text-anchor="middle" transform="rotate(-14 21 66)" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="10" fill="${INK}">$</text>`,
  },
  {
    id: 'noodles',
    name: 'Дошик',
    color: '#F5C04E',
    caption: ['УЖИН', 'ПОСЛЕ СТОПА'],
    tilt: -5,
    art: `
      <path d="M44 20c-2-6 2-10 0-16M58 18c-2-6 2-10 0-16" ${l('#E3E9F4', 2.4)}/>
      <path d="M88 4L72 34M92 6L76 36" ${l('#C9D3E6', 2.4)}/>
      <path d="M32 30c6-6 12 2 18-4s12 2 18-4 12 2 18-4" ${l('#F5D17A', 3.2)}/>
      <path d="M28 30h64l-9 50H37z" ${o('#F4F7FF')}/>
      <path d="M31 44h58l-2 14H33z" ${o('#FF5470', 2.2)}/>
      <text x="60" y="55" text-anchor="middle" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="9" fill="#FFFFFF">ДОШИК</text>
      <path d="M26 30h68" ${l(INK, 3)}/>
      <path d="M40 66q4-3 8 0M60 66q4-3 8 0" ${l('#3A4560', 2)}/>`,
  },
  {
    id: 'leverage',
    name: 'Плечо x100',
    color: '#FF8A3D',
    caption: ['ПЛЕЧО x100', 'ВСЁ ПО ПЛАНУ'],
    tilt: 4,
    art: `
      <path d="M60 30c0-10 6-16 14-18" ${l('#8A6A4A', 3)}/>
      <polygon points="${burst(78, 10, 9, 4, 6)}" ${o('#F5C04E', 2)}/>
      <rect x="34" y="30" width="17" height="48" rx="4" ${o('#FF5470')}/>
      <rect x="51.5" y="30" width="17" height="48" rx="4" ${o('#FF5470')}/>
      <rect x="69" y="30" width="17" height="48" rx="4" ${o('#FF5470')}/>
      <rect x="31" y="47" width="58" height="13" rx="3" ${o('#F5C04E', 2.4)}/>
      <text x="60" y="57.5" text-anchor="middle" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="11" fill="${INK}">x100</text>
      <path d="M38 34v8M55.5 34v8M73 34v8" ${l('#FFFFFF', 2)}/>
      ${spark(20, 24, 4, '#F5C04E')}${spark(100, 30, 4)}`,
  },
  {
    id: 'clown',
    name: 'Гений',
    color: '#A78BFA',
    caption: ['ГЕНИЙ', 'ТРЕЙДИНГА'],
    tilt: -3,
    art: `
      <circle cx="31" cy="38" r="13" ${o('#A78BFA')}/><circle cx="89" cy="38" r="13" ${o('#A78BFA')}/>
      <circle cx="40" cy="24" r="10" ${o('#2EE6B0')}/><circle cx="80" cy="24" r="10" ${o('#2EE6B0')}/>
      <path d="M28 76c8-8 56-8 64 0-8 6-56 6-64 0z" ${o('#FF5470')}/>
      <circle cx="60" cy="46" r="27" ${o('#F4F7FF')}/>
      <path d="M44 32l6 6-6 6-6-6zM76 32l6 6-6 6-6-6z" fill="#67E8F9"/>
      <circle cx="46" cy="38" r="3" fill="${INK}"/><circle cx="74" cy="38" r="3" fill="${INK}"/>
      <circle cx="60" cy="50" r="7" ${o('#FF5470', 2.2)}/>
      <circle cx="58" cy="48" r="2" fill="#FFFFFF" opacity="0.8"/>
      <path d="M44 58q16 14 32 0" ${o('#FF5470', 2.4)}/>
      <path d="M49 59q11 7 22 0" fill="#FFFFFF"/>`,
  },
  {
    id: 'secret',
    name: 'Жене ни слова',
    color: '#A78BFA',
    caption: ['ЖЕНЕ', 'НИ СЛОВА'],
    tilt: 4,
    art: `
      <circle cx="60" cy="44" r="31" ${o('#FFD6B8')}/>
      <path d="M36 26l12 4M84 26l-12 4" ${l(INK, 3)}/>
      <ellipse cx="46" cy="38" rx="7" ry="8" ${o('#FFFFFF', 2.2)}/><ellipse cx="74" cy="38" rx="7" ry="8" ${o('#FFFFFF', 2.2)}/>
      <circle cx="42" cy="39" r="3.4" fill="${INK}"/><circle cx="70" cy="39" r="3.4" fill="${INK}"/>
      <path d="M50 58h20" ${l(INK, 2.6)}/>
      <rect x="55" y="44" width="10" height="30" rx="5" ${o('#FFD6B8', 2.4)}/>
      <path d="M48 70c0-6 4-8 8-8h8c4 0 8 2 8 8v8H48z" ${o('#FFD6B8', 2.4)}/>
      <path d="M98 22c0 4-3 6-5 6s-5-2-5-6c0-3 5-9 5-9s5 6 5 9z" ${o('#8FD3FF', 2)}/>
      <path d="M8 34c0-8 6-12 14-12h6c8 0 12 5 12 11s-4 11-12 11h-4l-8 6 1-6c-6-1-9-5-9-10z" ${o('#F4F7FF', 2)}/>
      <text x="24" y="38" text-anchor="middle" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="9" fill="${INK}">ТСС</text>`,
  },
  {
    id: 'copium',
    name: 'Копиум',
    color: '#67E8F9',
    caption: ['ВДЫХАЮ', 'КОПИУМ'],
    tilt: -5,
    art: `
      <rect x="34" y="4" width="12" height="9" rx="2" ${o('#9AA6BD', 2.2)}/>
      <rect x="24" y="12" width="32" height="70" rx="14" ${o('#67E8F9')}/>
      <path d="M30 26v40" ${l('#FFFFFF', 2.6)}/>
      <rect x="31" y="36" width="18" height="30" rx="3" ${o('#F4F7FF', 2)}/>
      <text x="40" y="51" text-anchor="middle" transform="rotate(-90 40 51)" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="7.5" fill="${INK}">COPIUM</text>
      <path d="M40 4c0-4 20-6 36 6 10 8 12 20 10 30" ${l('#3A4560', 3)}/>
      <circle cx="90" cy="54" r="19" ${o('#FFD6B8')}/>
      <path d="M80 46q4-3 8 0M94 46q4-3 8 0" ${l(INK, 2.4)}/>
      <ellipse cx="90" cy="62" rx="10" ry="8" ${o('#9BE7F5', 2.2)}/>
      <path d="M80 60l-4-2M100 60l4-2" ${l(INK, 1.6)}/>
      ${spark(104, 30, 4)}${spark(70, 24, 3, '#67E8F9')}`,
  },
  {
    id: 'margin',
    name: 'Маржин-колл',
    color: '#FF5470',
    caption: ['МАРЖИН-КОЛЛ', 'НА ПРОВОДЕ'],
    tilt: 5,
    art: `
      <path d="M22 76l9-28h58l9 28z" ${o('#FF5470')}/>
      <circle cx="60" cy="62" r="12" ${o('#F4F7FF', 2.4)}/>
      <circle cx="60" cy="54" r="1.8" fill="${INK}"/><circle cx="67" cy="57" r="1.8" fill="${INK}"/><circle cx="68" cy="65" r="1.8" fill="${INK}"/>
      <circle cx="53" cy="57" r="1.8" fill="${INK}"/><circle cx="52" cy="65" r="1.8" fill="${INK}"/><circle cx="60" cy="69" r="1.8" fill="${INK}"/>
      <path d="M20 36c0-10 8-16 18-16h44c10 0 18 6 18 16l-12 5c-1-4-3-6-7-6H39c-4 0-6 2-7 6z" transform="rotate(-6 60 30)" ${o('#FF5470')}/>
      <path d="M12 26l-6-4M10 38l-8 0M108 26l6-4M110 38l8 0" ${l('#F5C04E', 3)}/>
      <path d="M40 14l-3-8M80 14l3-8M60 12V4" ${l('#F5C04E', 3)}/>`,
  },
  {
    id: 'factory',
    name: 'На завод',
    color: '#F5C04E',
    caption: ['ОБРАТНО', 'НА ЗАВОД'],
    tilt: -4,
    art: `
      <path d="M26 60c0-24 15-38 34-38s34 14 34 38z" ${o('#F5C04E')}/>
      <path d="M52 24c0 12 1 24 2 36M68 24c0 12-1 24-2 36" ${l('#E0A93A', 2.6)}/>
      <path d="M36 42c2-8 8-14 16-16" ${l('#FFFFFF', 2.6)}/>
      <rect x="16" y="58" width="88" height="10" rx="5" ${o('#E0A93A')}/>
      <circle cx="60" cy="42" r="7" ${o('#F4F7FF', 2)}/>
      <text x="60" y="46" text-anchor="middle" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="9" fill="${INK}">₿</text>
      <path d="M44 76q4 4 8 0M68 76q4 4 8 0" ${l(INK, 2.6)}/>
      <path d="M58 82q2-2 4 0" ${l(INK, 2)}/>
      <path d="M96 12c-4 0-7 3-7 7l-14 14 4 4 14-14c4 0 7-3 7-7l-4 3-3-3z" ${o('#C9D3E6', 2.2)}/>`,
  },
  {
    id: 'knock',
    name: 'Снизу стучат',
    color: '#3BA7FF',
    caption: ['СНИЗУ', 'ПОСТУЧАЛИ'],
    tilt: 4,
    art: `
      <path d="M10 4l12 7 9-5 11 12 8-4 7 16" ${l('#FF5470', 3.4)}/>
      <path d="M53 26l4-4 2 6" ${l('#FF5470', 3)}/>
      <rect x="6" y="30" width="108" height="13" rx="2" ${o('#8A6A4A')}/>
      <path d="M30 30v13M58 30v13M86 30v13" ${l('#6B4A3A', 2)}/>
      <path d="M68 43l3 4 3-4 3 5" ${l(INK, 2)}/>
      <path d="M62 52c0-4 3-6 6-6h12c3 0 6 2 6 6v10c0 4-3 6-6 6H68c-3 0-6-2-6-6z" ${o('#FFD6B8')}/>
      <path d="M66 52h16M66 57h16" ${l('#D99A6C', 1.6)}/>
      <path d="M58 48l-6-3M90 48l6-3M56 58h-7M92 58h7" ${l('#F5C04E', 2.6)}/>
      <text x="26" y="62" text-anchor="middle" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="11" fill="#FFFFFF" stroke="${INK}" stroke-width="0.8">ТУК</text>
      <text x="104" y="66" text-anchor="middle" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="9" fill="#FFFFFF" stroke="${INK}" stroke-width="0.7">ТУК</text>`,
  },
];

const BY_ID = new Map(STICKERS.map((sticker) => [sticker.id, sticker]));

/** Префикс, по которому текст иконки узнаётся как наш стикер. */
export const STICKER_PREFIX = 'anz:';

export function stickerOf(text: string | undefined): Sticker | null {
  if (!text?.startsWith(STICKER_PREFIX)) return null;
  return BY_ID.get(text.slice(STICKER_PREFIX.length)) ?? null;
}

/** Системный жирный шрифт ярлыка: внешние шрифты в картинку-SVG не попадают. */
const TAG_FONT = `'Arial Black','Segoe UI Black','Helvetica Neue',Arial,sans-serif`;

/**
 * Ярлык с подписью — полоска фирменного цвета наискосок.
 *
 * Ширина считается по числу букв: померить текст внутри картинки-SVG
 * нечем, а у жирных прописных ширина знака почти одинакова.
 */
function tag(sticker: Sticker): string {
  const lines = sticker.caption;
  const size = lines.length > 1 ? 10.5 : 12.5;
  const longest = Math.max(...lines.map((line) => line.length));
  const width = Math.min(112, Math.max(54, longest * size * 0.74 + 20));
  const lineHeight = size + 2;
  const height = lines.length * lineHeight + 9;
  const top = 116 - height;
  const text = lines
    .map(
      (line, index) =>
        `<text x="60" y="${(top + 5 + size + index * lineHeight - 1).toFixed(1)}" text-anchor="middle" font-family="${TAG_FONT}" font-weight="900" font-size="${size}" fill="${INK}" letter-spacing="0.2">${line}</text>`,
    )
    .join('');
  return `<g transform="rotate(-3 60 ${top + height / 2})">
    <rect x="${60 - width / 2}" y="${top}" width="${width}" height="${height}" rx="7" ${o(sticker.color, 2.4)}/>
    <path d="M${60 - width / 2 + 7} ${top + 3.5}h${width - 14}" fill="none" stroke="#FFFFFF" stroke-opacity="0.45" stroke-width="2" stroke-linecap="round"/>
    ${text}
  </g>`;
}

/**
 * Полный SVG стикера.
 *
 * Белая кромка по контуру — расширенный силуэт рисунка (как вырубка у
 * настоящего стикера), под ней мягкая тень; весь стикер чуть повёрнут.
 * Рисуется вдвое крупнее, чем показывается: на плотных экранах резко.
 */
export function stickerSvg(sticker: Sticker): string {
  const art = sticker.art.replace(/\s*\n\s*/g, '');
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-6 -6 132 132" width="264" height="264">
    <defs>
      <filter id="die" x="-20%" y="-20%" width="140%" height="140%" color-interpolation-filters="sRGB">
        <feMorphology in="SourceAlpha" operator="dilate" radius="3.6" result="grow"/>
        <feFlood flood-color="#F6F8FF"/>
        <feComposite in2="grow" operator="in" result="edge"/>
        <feGaussianBlur in="grow" stdDeviation="2.6"/>
        <feOffset dy="2.6" result="soft"/>
        <feFlood flood-color="#000000" flood-opacity="0.5"/>
        <feComposite in2="soft" operator="in" result="shadow"/>
        <feMerge><feMergeNode in="shadow"/><feMergeNode in="edge"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
    </defs>
    <g filter="url(#die)" transform="rotate(${sticker.tilt} 60 60)">
      <g transform="translate(0 2) scale(0.92) translate(5.2 0)">${art}</g>
      ${tag(sticker)}
    </g>
  </svg>`;
}

export function stickerUrl(sticker: Sticker): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(stickerSvg(sticker))}`;
}

/*
 * Картинки собираются заранее: холст рисует синхронно, и стикер,
 * картинка которого ещё грузится, мигнул бы пустотой.
 */
const images = new Map<string, HTMLImageElement>();
if (typeof Image !== 'undefined') {
  for (const sticker of STICKERS) {
    const image = new Image();
    image.src = stickerUrl(sticker);
    images.set(sticker.id, image);
  }
}

/** Позвать, когда догрузится любая картинка стикера: холсту пора перерисоваться. */
export function onStickerLoad(callback: () => void): void {
  for (const image of images.values()) {
    if (!image.complete) image.addEventListener('load', callback, { once: true });
  }
}

export function stickerImage(sticker: Sticker): HTMLImageElement | null {
  const image = images.get(sticker.id);
  return image && image.complete && image.naturalWidth > 0 ? image : null;
}
